import DesignTokens from '@tableau/design-tokens';
import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, FilteringList, ListItem, standardItemFilter, TabStyles } from '@tableau/widgets';
import React from 'react';

import { ReactModalOptions } from '~commonInfra/dialogs/ReactModalService';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { applyColor }from '~src/style/ColorService';

import { Messages } from '../../../../../codegen/Localize';

export interface RunNowEnabledSiteDialogProps {
    runNowEnabledStates: {
        [site: string]: boolean;
    };
    updateSites: (newState: { [site: string]: boolean }) => void;
    updateAll: (state: boolean) => void;
    close: () => void;
}

export interface RunNowEnabledSiteDialogState {
    runNowEnabledStates: RunNowEnabledSiteDialogProps['runNowEnabledStates'];
    filterText: string;
}

const siteNameToListItem: (site: string) => ListItem = site => ({ value: site, testId: 'site-runnow-setting-' + site + '-Item' });

export class RunNowEnabledSiteDialog extends React.Component<RunNowEnabledSiteDialogProps, RunNowEnabledSiteDialogState> {
    public state = {
        runNowEnabledStates: this.props.runNowEnabledStates,
        filterText: '',
    };

    render(): React.ReactNode {
        const { filterText, runNowEnabledStates } = this.state;

        const siteNames = Object.keys(runNowEnabledStates);
        const selectedValues = siteNames.filter(siteName => runNowEnabledStates[siteName]);
        const items = siteNames.map(siteNameToListItem);

        const filteredItems = standardItemFilter(items, filterText);
        const siteStates = filteredItems.map(item => runNowEnabledStates[item.value]);
        const selectedSiteCount = siteStates.filter(b => b).length;
        const allFilteredSitesChecked = !siteStates.includes(false);
        const allFilteredSitesUnchecked = !siteStates.includes(true);
        const renderedItems = filteredItems.length > 0
            ? filteredItems
            : [{ value: Messages.runNowSiteNotFound(), disabled: true }];
        return <>
            <CheckBoxWithLabelWidget
                label={allFilteredSitesChecked
                    ? Messages.runNowSiteSelectionDialogDisableAll()
                    : Messages.runNowSiteSelectionDialogEnableAll()}
                checked={allFilteredSitesChecked}
                indeterminate={!allFilteredSitesChecked && !allFilteredSitesUnchecked}
                disabled={siteStates.length === 0}
                testId="all-sites-runnnow-setting"
                handleChange={(checked: boolean) => {
                    const newState: { [site: string]: boolean } = {};
                    filteredItems.forEach(item => {
                        const site = item.value;
                        newState[site] = checked;
                    });
                    this.setState({
                        runNowEnabledStates: {
                            ...runNowEnabledStates,
                            ...newState,
                        },
                    });
                }}
            />
            <FilteringList
                items={renderedItems}
                style={{
                    border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles!.Colors!.L6)}`,
                    maxHeight: '200px',
                }}
                searchText={filterText}
                testId="site-selection-list"
                onFilterChange={filterText => this.setState({ filterText })}
                onSelect={(site: string) => this.setState({
                    runNowEnabledStates: {
                        ...runNowEnabledStates,
                        [site]: !runNowEnabledStates[site],
                    }
                })}
                selectedValues={new Set(selectedValues)}
            />
            <div style={{ paddingTop: '9px' }}>
                {Messages.runNowServerSettingSitesSelected({
                    TOTAL_SITES_COUNT_LOC: L10n.localizedCount(siteStates.length),
                    SELECTED_SITES_COUNT_LOC: L10n.localizedCount(selectedSiteCount)
                })}
            </div>
            <div>
                <DialogFooterButtonsWidget
                    buttons={[{
                        handleClick: () => {
                            this.props.close();
                        },
                        buttonType: ButtonType.Outline,
                        children: Messages.runNowSiteSelectionDialogCancel(),
                        testId: 'footer-cancel'
                    }, {
                        handleClick: () => {
                            // Update the site settings
                            this.props.updateSites(runNowEnabledStates);
                            this.props.close();
                        },
                        buttonType: ButtonType.Primary,
                        children: Messages.runNowSiteSelectionDialogSave(),
                        testId: 'footer-save'
                    }]}
                    testId="footer"
                />
            </div>
        </>;
    }
}

// focus the filter box (instead of the default "Check All" checkbox) when the dialog opens
const findInputToFocus = (dialogElem: HTMLElement) => dialogElem.querySelector<HTMLElement>('input[role="combobox"]');

export function runNowEnabledSiteDialogProps(props: RunNowEnabledSiteDialogProps): ReactModalOptions {
    return {
        title: Messages.runNowSiteSelectionDialogTitle(),
        testId: 'run-now-enabled-site-dialog',
        type: DialogType.NormalWithTitleBar,
        positioningBehavior: DialogPositioningBehavior.Centered,
        dialogContent: <RunNowEnabledSiteDialog {...props} />,
        disableAutoFocus: findInputToFocus,
    };
}
